import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Input, Select } from '@components/ui';
import { AddressListManager } from './AddressListManager';
import { customerService } from '../services/customerService';
import { formatPhoneDisplay, validatePhoneDGI, PHONE_HELPER_TEXT } from '@/lib/utils/phone';
import { PhoneFormatHelp } from '@/components/PhoneFormatHelp';

import type { Customer, Address } from '../types/customer.types';

const customerSchema = z.object({
  name: z.string().min(2, 'Mínimo 2 caracteres').max(200, 'Máximo 200 caracteres'),
  taxIdType: z.string().min(1, 'El tipo de identificación es requerido'),
  document_type: z.string().min(1, 'El tipo de documento es requerido'),
  // Tipo de RUC: solo requerido para clientes nacionales (no extranjeros)
  recep_type: z.string().optional().or(z.literal('')),
  taxId: z.string().min(1, 'La identificación es requerida').max(50, 'Máximo 50 caracteres'),
  taxIdSub: z.string().max(2, 'Máximo 2 caracteres').optional().or(z.literal('')),
  email: z.string().min(1, 'El email es requerido').email('Email inválido').max(100, 'Máximo 100 caracteres'),
  phone: z.string().max(16, 'Máximo 16 caracteres').superRefine((val, ctx) => {
    const result = validatePhoneDGI(val, { required: false });
    if (!result.valid && result.error) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: result.error });
    }
  }),
  isTaxExempt: z.boolean().optional(),
  taxExemptionCode: z.string().optional().nullable(),
}).refine(
  (data) => {
    // Tipo de RUC es obligatorio solo si NO es extranjero
    if (data.taxIdType !== 'EXTRANJERO') {
      return !!data.recep_type && data.recep_type !== '';
    }
    return true;
  },
  { message: 'El tipo de RUC es requerido', path: ['recep_type'] }
);

type CustomerFormData = z.infer<typeof customerSchema>;

interface CustomerFormProps {
  customer?: Customer;
  onSubmit: (data: Partial<Customer>) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function CustomerForm({ customer, onSubmit, onCancel, isLoading }: CustomerFormProps) {
  const [addresses, setAddresses] = useState<Address[]>(
    customer?.address || []
  );
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState<string>('');

  // Map tax_id_type code to form value
  const mapTaxIdTypeCodeToForm = (code: string | undefined): string => {
    if (!code) return 'CONSUMIDOR_FINAL';
    const mapping: Record<string, string> = {
      '01': 'CONTRIBUYENTE',
      '02': 'CONSUMIDOR_FINAL',
      '03': 'GOBIERNO',
      '04': 'EXTRANJERO',
    };
    return mapping[code] || code; // Si ya es texto, retornarlo
  };

  // Map type code to document_type form value
  const mapTypeCodeToForm = (code: string | undefined): string => {
    if (!code) return 'RUC';
    const mapping: Record<string, string> = {
      '01': 'RUC',
      '02': 'CEDULA',
      '03': 'PASAPORTE',
      '04': 'OTRO',
    };
    return mapping[code] || code; // Si ya es texto, retornarlo
  };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CustomerFormData>({
    resolver: zodResolver(customerSchema),
    mode: 'onChange',
    defaultValues: {
      name: customer?.name || '',
      taxId: customer?.taxId || '',
      taxIdSub: customer?.taxIdSub || '',
      taxIdType: mapTaxIdTypeCodeToForm(customer?.taxIdType),
      document_type: mapTypeCodeToForm(customer?.type),
      recep_type: customer?.recep_type || '1',
      email: customer?.email || '',
      phone: customer?.phone || '',
      isTaxExempt: customer?.isTaxExempt || false,
      taxExemptionCode: customer?.taxExemptionCode || null,
    },
  });

  const isTaxExempt = watch('isTaxExempt');
  const taxId = watch('taxId');
  const taxIdSub = watch('taxIdSub');
  const recep_type = watch('recep_type');
  const taxIdType = watch('taxIdType');
  const isExtranjero = taxIdType === 'EXTRANJERO';

  // Map dTipoRec to taxIdType codes
  const mapTipoRecToTaxIdType = (tipoRec: string): string => {
    const mapping: Record<string, string> = {
      'Contribuyente': 'CONTRIBUYENTE',
      'Consumidor Final': 'CONSUMIDOR_FINAL',
      'Gobierno': 'GOBIERNO',
      'Extranjero': 'EXTRANJERO',
    };
    return mapping[tipoRec] || 'CONSUMIDOR_FINAL';
  };

  const handleVerifyRuc = async () => {
    if (!taxId || !taxIdSub || !recep_type) {
      setVerificationMessage('Por favor complete RUC, DV y Tipo de RUC');
      return;
    }

    setIsVerifying(true);
    setVerificationMessage('');

    try {
      const result = await customerService.verifyRucDV(taxId, taxIdSub, recep_type);

      if (result.success && result.data) {
        // Auto-fill name and taxIdType from DGI
        setValue('name', result.data.nombre);
        setValue('taxIdType', mapTipoRecToTaxIdType(result.data.tipoRec));

        if (result.tipoRucCorregido && result.data.tipoRuc) {
          setValue('recep_type', result.data.tipoRuc);
          const tipoLabel = result.data.tipoRuc === '2' ? 'Jurídico' : 'Natural';
          setVerificationMessage(
            `✓ Verificado como ${tipoLabel}: ${result.data.nombre} (${result.data.tipoRec}). Se corrigió el Tipo de RUC automáticamente.`
          );
        } else {
          setVerificationMessage(`✓ Verificado: ${result.data.nombre} (${result.data.tipoRec})`);
        }
      } else {
        setVerificationMessage(`✗ ${result.message}`);
      }
    } catch (error) {
      setVerificationMessage('✗ Error al verificar RUC con DGI');
      console.error('Error verifying RUC:', error);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleFormSubmit = (data: CustomerFormData) => {
    // Normalize taxIdSub: convert empty string to undefined
    const normalizedTaxIdSub = data.taxIdSub && data.taxIdSub.trim() !== '' ? data.taxIdSub : undefined;

    // Combine form data with addresses
    const isExtranjeroSubmit = data.taxIdType === 'EXTRANJERO';

    const submitData: Partial<Customer> = {
      name: data.name,
      taxIdType: data.taxIdType,
      taxId: data.taxId,
      taxIdSub: isExtranjeroSubmit ? undefined : normalizedTaxIdSub,
      email: data.email,
      phone: data.phone,
      type: data.document_type, // Map document_type to type (what backend expects)
      recep_type: isExtranjeroSubmit ? undefined : data.recep_type,
      isTaxExempt: data.isTaxExempt,
      taxExemptionCode: data.taxExemptionCode,
      address: addresses,
    };

    console.log('📤 Customer Form Submit Data:', submitData);
    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col h-full">
      {/* Two Column Layout - Scrollable area */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1 min-h-0 overflow-hidden">
        {/* Left Column - Basic Information */}
        <div className="flex flex-col min-h-0 overflow-hidden">
          <h3 className="text-lg font-medium text-secondary-900 dark:text-white bg-white dark:bg-secondary-800 py-2 z-10 flex-shrink-0">
            Información Básica
          </h3>
          <div className="space-y-4 pb-4 overflow-y-auto pr-2">
            <Input
              label="Nombre / Razón Social"
              placeholder="Nombre del cliente"
              error={errors.name?.message}
              maxLength={200}
              required
              {...register('name')}
            />

            <Select
              label="Tipo de Identificación"
              error={errors.taxIdType?.message}
              required
              {...register('taxIdType')}
            >
              <option value="">Seleccione un tipo</option>
              <option value="CONSUMIDOR_FINAL">Consumidor Final</option>
              <option value="CONTRIBUYENTE">Contribuyente</option>
              <option value="GOBIERNO">Gobierno</option>
              <option value="EXTRANJERO">Extranjero</option>
            </Select>

            {!isExtranjero && (
              <Select
                label="Tipo de RUC"
                error={errors.recep_type?.message}
                required
                {...register('recep_type')}
              >
                <option value="">Seleccione un tipo</option>
                <option value="1">Natural</option>
                <option value="2">Jurídico</option>
              </Select>
            )}

            <div className="space-y-4">
              <Select
                label="Tipo de Documento"
                error={errors.document_type?.message}
                required
                {...register('document_type')}
              >
                <option value="">Seleccione un tipo</option>
                {isExtranjero ? (
                  <>
                    <option value="PASAPORTE">Pasaporte</option>
                    <option value="OTRO">Otro</option>
                  </>
                ) : (
                  <>
                    <option value="RUC">RUC</option>
                    <option value="CEDULA">Cédula</option>
                    <option value="PASAPORTE">Pasaporte</option>
                    <option value="OTRO">Otro</option>
                  </>
                )}
              </Select>

              {isExtranjero ? (
                <Input
                  label="Pasaporte o ID Tributario Extranjero"
                  placeholder="Ej: 081-101182-0004L"
                  error={errors.taxId?.message}
                  maxLength={50}
                  required
                  {...register('taxId')}
                />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-2">
                    <Input
                      label="Número de Identificación"
                      placeholder="0000000-0-000000"
                      error={errors.taxId?.message}
                      maxLength={20}
                      required
                      {...register('taxId')}
                    />
                  </div>
                  <div className="col-span-1">
                    <Input
                      label="DV"
                      placeholder="00"
                      error={errors.taxIdSub?.message}
                      maxLength={2}
                      {...register('taxIdSub')}
                    />
                  </div>
                </div>
              )}

              {/* Verify RUC Button - Solo para clientes nacionales */}
              {!isExtranjero && (
                <div className="space-y-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleVerifyRuc}
                    isLoading={isVerifying}
                    className="w-full"
                  >
                    {isVerifying ? 'Verificando...' : 'Verificar RUC con DGI'}
                  </Button>
                  {verificationMessage && (
                    <p className={`text-sm ${verificationMessage.startsWith('✓') ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {verificationMessage}
                    </p>
                  )}
                </div>
              )}
            </div>

            <Input
              label="Email"
              type="email"
              placeholder="cliente@ejemplo.com"
              error={errors.email?.message}
              maxLength={100}
              required
              {...register('email')}
            />

            <Input
              label="Teléfono"
              labelExtra={<PhoneFormatHelp />}
              placeholder="6789-1234"
              error={errors.phone?.message}
              helperText={PHONE_HELPER_TEXT}
              inputMode="tel"
              maxLength={16}
              {...register('phone', {
                onBlur: (e) => {
                  const value = e.target.value;
                  if (!value || !value.trim()) return;
                  const formatted = formatPhoneDisplay(value);
                  if (formatted && formatted !== value) {
                    setValue('phone', formatted, { shouldValidate: true, shouldDirty: true });
                  }
                },
              })}
            />

            {/* Tax Exemption */}
            <div className="space-y-3 pt-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="mr-2 rounded border-secondary-300 dark:border-secondary-600"
                  {...register('isTaxExempt')}
                />
                <span className="text-sm text-secondary-700 dark:text-secondary-300">
                  Cliente exento de impuestos
                </span>
              </label>

              {isTaxExempt && (
                <Input
                  label="Código de Exención Fiscal"
                  placeholder="Código de exención"
                  error={errors.taxExemptionCode?.message as string | undefined}
                  {...register('taxExemptionCode')}
                />
              )}
            </div>
          </div>{/* End of scroll container */}
        </div>{/* End of left column */}

        {/* Right Column - Addresses */}
        <div className="border-l border-secondary-200 dark:border-secondary-700 pl-6 flex flex-col min-h-0 overflow-hidden">
          <div className="overflow-y-auto pr-2">{/* Scroll container for addresses */}
            <AddressListManager
              addresses={addresses}
              onChange={setAddresses}
            />
          </div>
        </div>
      </div>

      {/* Form Actions - Always visible at bottom */}
      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-3 pt-4 mt-4 border-t border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-800 flex-shrink-0 [&>*]:w-full sm:[&>*]:w-auto">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" variant="primary" isLoading={isLoading}>
          {customer ? 'Actualizar' : 'Crear'} Cliente
        </Button>
      </div>
    </form>
  );
}
